import { useCallback, useEffect, useState } from 'react'
import { useI18n } from '../lib/i18n'

/**
 * Mesa de administração.
 *
 * Só aparece para quem administra, e quem administra vem de variável de
 * ambiente no servidor — não há promoção pelo app. Esconder a aba aqui não é
 * controle de acesso: o servidor confere em cada endpoint. É não oferecer uma
 * porta que não abriria.
 *
 * A exclusão de conta é irreversível e leva junto, em cascata, logbook,
 * créditos, estação, encontros e pedidos. Por isso ela pede confirmação
 * digitada com o indicativo: um toque errado numa lista não pode apagar o
 * histórico de uma pessoa. Editar e bloquear não têm esse selo — são
 * reversíveis, então não precisam do mesmo atrito.
 */

interface Conta {
  id: string
  email: string
  callsign: string
  class: string
  kind: string
  tem_senha: boolean
  admin: boolean
  blocked_login: boolean
  blocked_tx: boolean
  criada_em: string
  visto_em: string
}

interface Pedido {
  id: string
  callsign: string
  class: string
  status: string
  handle?: string
  tem_documento?: boolean
}

export function Admin({ meuID }: { meuID: string }) {
  const { t } = useI18n()
  const CLASSES = [
    { valor: '', nome: t.admin.swl },
    { valor: 'C', nome: t.admin.classeC },
    { valor: 'B', nome: t.admin.classeB },
    { valor: 'A', nome: t.admin.classeA },
  ] as const
  const [contas, setContas] = useState<Conta[] | null>(null)
  const [pedidos, setPedidos] = useState<Pedido[]>([])
  const [confirmar, setConfirmar] = useState<Conta | null>(null)
  const [digitado, setDigitado] = useState('')
  const [editando, setEditando] = useState<Conta | null>(null)
  const [formClass, setFormClass] = useState('')
  const [formCallsign, setFormCallsign] = useState('')
  const [formEmail, setFormEmail] = useState('')
  const [formSenha, setFormSenha] = useState('')
  const [formBloqueiaLogin, setFormBloqueiaLogin] = useState(false)
  const [formBloqueiaTx, setFormBloqueiaTx] = useState(false)
  const [msg, setMsg] = useState<string | null>(null)
  const [err, setErr] = useState<string | null>(null)
  const [busy, setBusy] = useState(false)

  const load = useCallback(() => {
    fetch('/api/admin/contas')
      .then((r) => (r.ok ? r.json() : Promise.reject(new Error(String(r.status)))))
      .then((d: { contas: Conta[] }) => setContas(d.contas ?? []))
      .catch(() => setErr(t.admin.erroListar))
    fetch('/api/admin/pedidos?status=pendente')
      .then((r) => (r.ok ? r.json() : { claims: [] }))
      .then((d: { claims?: Pedido[] }) => setPedidos(d.claims ?? []))
      .catch(() => {})
  }, [t.admin.erroListar])

  useEffect(load, [load])

  const excluir = async (c: Conta) => {
    setBusy(true)
    setErr(null)
    setMsg(null)
    try {
      const r = await fetch(`/api/admin/contas/${c.id}`, { method: 'DELETE' })
      const d = (await r.json()) as { message?: string }
      if (!r.ok) {
        setErr(d.message ?? t.admin.erroExcluir)
        return
      }
      setMsg(`${c.callsign} ${t.admin.excluida}`)
      setConfirmar(null)
      setDigitado('')
      load()
    } catch {
      setErr(t.admin.erroServidor)
    } finally {
      setBusy(false)
    }
  }

  const decidir = async (p: Pedido, status: 'aprovado' | 'recusado') => {
    setBusy(true)
    try {
      await fetch(`/api/admin/pedidos/${p.id}`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ status, class: p.class }),
      })
      load()
    } finally {
      setBusy(false)
    }
  }

  const abrirEdicao = (c: Conta) => {
    setEditando(c)
    setFormClass(c.class)
    setFormCallsign(c.callsign)
    setFormEmail(c.email)
    setFormSenha('')
    setFormBloqueiaLogin(c.blocked_login)
    setFormBloqueiaTx(c.blocked_tx)
    setErr(null)
    setMsg(null)
  }

  const salvarEdicao = async () => {
    if (!editando) return
    setBusy(true)
    setErr(null)
    setMsg(null)
    try {
      const body: Record<string, unknown> = {
        class: formClass,
        callsign: formCallsign,
        email: formEmail,
        blocked_login: formBloqueiaLogin,
        blocked_tx: formBloqueiaTx,
      }
      // Senha em branco significa "não mexer" — só entra no corpo se a
      // pessoa realmente digitou uma nova.
      if (formSenha) body.senha = formSenha

      const r = await fetch(`/api/admin/contas/${editando.id}`, {
        method: 'PATCH',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(body),
      })
      const d = (await r.json()) as { message?: string }
      if (!r.ok) {
        setErr(d.message ?? t.admin.erroSalvar)
        return
      }
      setMsg(`${formCallsign} ${t.admin.atualizada}`)
      setEditando(null)
      load()
    } catch {
      setErr(t.admin.erroServidor)
    } finally {
      setBusy(false)
    }
  }

  if (!contas) return <section className="station"><p className="st-hint">{t.admin.carregando}</p></section>

  return (
    <>
      <section className="station">
        <h2 className="st-titulo">{t.admin.pedidosTitulo}</h2>
        {pedidos.length === 0 ? (
          <p className="st-hint">{t.admin.semPedidos}</p>
        ) : (
          <ul className="adm-lista">
            {pedidos.map((p) => (
              <li key={p.id}>
                <div>
                  <strong>{p.callsign}</strong>
                  <span className={p.tem_documento ? 'pill on' : 'pill fraco'}>
                    {p.tem_documento ? t.admin.comDocumento : t.admin.semDocumento}
                  </span>
                  <small>
                    {p.handle ?? '—'} · {t.admin.classe} {p.class || '—'}
                  </small>
                </div>
                <div className="adm-acoes">
                  {p.tem_documento && (
                    <a
                      className="link"
                      href={`/api/admin/pedidos/${p.id}/documento`}
                      target="_blank"
                      rel="noreferrer"
                    >
                      {t.admin.verDocumento}
                    </a>
                  )}
                  <button disabled={busy} onClick={() => void decidir(p, 'aprovado')}>
                    {t.admin.aprovar}
                  </button>
                  <button disabled={busy} onClick={() => void decidir(p, 'recusado')}>
                    {t.admin.recusar}
                  </button>
                </div>
              </li>
            ))}
          </ul>
        )}
        <p className="st-hint">{t.admin.pedidosHint}</p>
      </section>

      <section className="station">
        <h2 className="st-titulo">{t.admin.contasTitulo} ({contas.length})</h2>
        {msg && <p className="st-ok">{msg}</p>}
        {err && <p className="denial">{err}</p>}

        <ul className="adm-lista">
          {contas.map((c) => (
            <li key={c.id}>
              <div>
                <strong>{c.callsign}</strong>
                {c.admin && <span className="pill">{t.admin.admin}</span>}
                {c.blocked_login && <span className="pill adm-perigo">{t.admin.loginBloqueado}</span>}
                {c.blocked_tx && <span className="pill adm-perigo">{t.admin.txBloqueado}</span>}
                <small>
                  {c.email} · {c.kind}
                  {c.class ? ` · ${t.admin.classe} ${c.class}` : ` · ${t.admin.ouvinte}`}
                  {c.tem_senha ? ` · ${t.admin.comSenha}` : ` · ${t.admin.soLink}`}
                </small>
              </div>
              <div className="adm-acoes">
                {c.id === meuID ? (
                  <small>{t.admin.voce}</small>
                ) : c.admin ? (
                  <small>{t.admin.administra}</small>
                ) : (
                  <>
                    <button disabled={busy} onClick={() => abrirEdicao(c)}>
                      {t.admin.editar}
                    </button>
                    <button disabled={busy} onClick={() => { setConfirmar(c); setDigitado('') }}>
                      {t.admin.excluir}
                    </button>
                  </>
                )}
              </div>
            </li>
          ))}
        </ul>
      </section>

      {editando && (
        <section className="station adm-editar">
          <h2 className="st-titulo">{t.admin.editarTitulo} {editando.callsign}</h2>

          <div className="st-field">
            <label htmlFor="ed-classe">{t.admin.classe}</label>
            <select id="ed-classe" value={formClass} onChange={(e) => setFormClass(e.target.value)}>
              {CLASSES.map((cl) => (
                <option key={cl.valor} value={cl.valor}>
                  {cl.nome}
                </option>
              ))}
            </select>
          </div>

          <div className="st-field">
            <label htmlFor="ed-indicativo">{t.admin.indicativoApelido}</label>
            <input
              id="ed-indicativo"
              value={formCallsign}
              onChange={(e) => setFormCallsign(e.target.value.toUpperCase())}
              autoCapitalize="characters"
              autoCorrect="off"
              spellCheck={false}
              maxLength={12}
            />
          </div>

          <div className="st-field">
            <label htmlFor="ed-email">{t.admin.email}</label>
            <input
              id="ed-email"
              type="email"
              value={formEmail}
              onChange={(e) => setFormEmail(e.target.value)}
            />
          </div>

          <div className="st-field">
            <label htmlFor="ed-senha">{t.admin.redefinirSenha}</label>
            <input
              id="ed-senha"
              type="password"
              value={formSenha}
              onChange={(e) => setFormSenha(e.target.value)}
              autoComplete="new-password"
            />
          </div>

          <label className="adm-toggle">
            <input
              type="checkbox"
              checked={formBloqueiaLogin}
              onChange={(e) => setFormBloqueiaLogin(e.target.checked)}
            />
            {t.admin.bloquearLogin}
          </label>
          <label className="adm-toggle">
            <input
              type="checkbox"
              checked={formBloqueiaTx}
              onChange={(e) => setFormBloqueiaTx(e.target.checked)}
            />
            {t.admin.bloquearTx}
          </label>
          <p className="st-hint">{t.admin.edicaoHint}</p>

          {err && <p className="denial">{err}</p>}

          <button className="st-save" disabled={busy} onClick={() => void salvarEdicao()}>
            {t.admin.salvar}
          </button>
          <button className="link" onClick={() => setEditando(null)}>
            {t.admin.cancelar}
          </button>
        </section>
      )}

      {confirmar && (
        <section className="station adm-confirma">
          <h2 className="st-titulo">{t.admin.excluirTitulo} {confirmar.callsign}?</h2>
          <p className="aviso">{t.admin.excluirAviso}</p>
          <div className="st-field">
            <label htmlFor="confirma">
              {t.admin.digitePara} <b>{confirmar.callsign}</b> {t.admin.paraConfirmar}
            </label>
            <input
              id="confirma"
              value={digitado}
              onChange={(e) => setDigitado(e.target.value.toUpperCase())}
              autoCapitalize="characters"
              autoCorrect="off"
              spellCheck={false}
            />
          </div>
          <button
            className="st-save adm-perigo"
            disabled={busy || digitado !== confirmar.callsign}
            onClick={() => void excluir(confirmar)}
          >
            {t.admin.excluirDefinitivamente}
          </button>
          <button className="link" onClick={() => setConfirmar(null)}>
            {t.admin.cancelar}
          </button>
        </section>
      )}
    </>
  )
}
